<template>
  <div v-if="editor" class="text-field" :class="{ 'text-field--borderless': this.borderless }">
    <div v-if="legend" class="legend">
      {{ legend }}
    </div>
    <div class="text-field__field generic-border--thin-border generic-border--focus-shadow">
      <TextFieldMenuBar
        v-if="!noMenu"
        class="text-field__field__header"
        :editor="editor"
        :hide-undo-redo="hideUndoRedo"
        :disabled="disabled"
        @mousedown="onMenuMousedown"
      />
      <EditorContent class="text-field__field__content" :editor="editor" />
    </div>
  </div>
</template>

<script>
/**
 * Advanced text field using TipTad editor.
 * Code taken from https://www.tiptap.dev/examples/collaborative-editing
 *
 * @vue-prop {Number} [editionTimeout=1000] - Default delay before emitting an input event on edition
 * @vue-prop {Boolean} [borderless=false] - Remove the border
 */

import { isEqual } from 'lodash';
import Placeholder from '@tiptap/extension-placeholder';
// TipTap
import { Editor, EditorContent } from '@tiptap/vue-2';
import { OneLineDoc, CustomStarterKit } from '@/plugins/tiptap';
// Components
import TextFieldMenuBar from '@/components/text-field/text-field-menu-bar.vue';

// Prevent triggering an input event from the tiptap 'update' event if a 'blur' event juste happened
const BLUR_TO_UPDATE_TIMEOUT = 200;


export default {

  name: 'TextField',

  components: {
    EditorContent,
    TextFieldMenuBar,
  },

  props: {
    value: {
      type: [String, Object],
      required: true,
    },
    legend: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    noMenu: Boolean,
    singleLine: Boolean,
    disabled: Boolean,
    hideUndoRedo: Boolean,
    borderless: Boolean,
    editionTimeout: {
      type: Number,
      default: 1000,
    },
  },

  data() {
    return {
      editor: null,
      editTimeout: null,
      // Detect clicking menu before 'blur' event
      menuWasClicked: false,
      // To save the content before destroying the component, set this boolean to true
      emitInputOnDestroy: false,
    };
  },

  watch: {
    value(newVal, oldVal) {
      if (this.editor && !isEqual(this.editor.getHTML(), newVal)) {
        this.createNewEditor();
      }
    },
    disabled() {
      this.editor.setOptions({ editable: !this.disabled });
    },
  },

  mounted() {
    this.createNewEditor();
  },

  beforeDestroy() {
    if (this.emitInputOnDestroy) {
      clearTimeout(this.editTimeout);
      this.emitInput();
    }
    this.editor.destroy();
  },

  methods: {
    /** Initialize the editor */
    createNewEditor() {
      if (this.editor) {
        this.editor.destroy();
      }
      const params = {
        content: this.value,
        extensions: [
          ...CustomStarterKit,
          Placeholder.configure({ placeholder: this.placeholder }),
        ],
      };
      if (this.singleLine) {
        params.useBuiltInExtensions = false;
        params.extensions = [
          ...params.extensions,
          OneLineDoc,
        ];
      }
      this.editor = new Editor(params);
      this.editor.on('focus', () => {
        this.$emit('focus');
      });
      this.editor.on('update', () => {
        clearTimeout(this.editTimeout);
        if (this.editionTimeout) {
          this.editTimeout = setTimeout(this.emitInput, this.editionTimeout);
        } else {
          this.emitInput();
        }
      });
      this.editor.on('blur', () => {
        this.emitInputOnDestroy = true;
        clearTimeout(this.editTimeout);
        if (this.menuWasClicked) {
          // Prevent triggering an input event from blur f it is followed
          // by an 'update' event (when clicking a menu item)
          this.editTimeout = setTimeout(this.emitInput, BLUR_TO_UPDATE_TIMEOUT);
        } else {
          this.$emit('blur');
          this.emitInput();
        }
      });
      this.editor.setOptions({ editable: !this.disabled });
    },
    /** Emit the new value */
    emitInput() {
      if (this.disabled) {
        return;
      }
      this.emitInputOnDestroy = false;
      this.$emit('input', this.editor.isEmpty ? '' : this.editor.getHTML());
    },
    /** Detect menu mousedown event to prevent emitting input on blur */
    onMenuMousedown() {
      if (!this.disabled) {
        this.menuWasClicked = true;
        setTimeout(() => {
          this.menuWasClicked = false;
        });
      }
    },
    doFocus() {
      this.editor.commands.focus('end');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vars.scss";

.text-field {
  display: flex;
  flex-direction: column;

  &__field {

    &.generic-border--thin-border {
      padding: 0px;
    }

    &__header {
      display: flex;
      align-items: center;
      flex: 0 0 auto;
      flex-wrap: wrap;
      padding: 0.25rem;
      border-bottom: 1px solid $color-gray-border;
    }

    &__content {
      padding: 0.25rem;
      flex: 1 1 auto;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
  }

  &--borderless {
    background: transparent;

    .text-field__field {
      border: none;
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.text-field {
  &__field.generic-border {
    background: transparent;
  }

  .ProseMirror {
    padding: 0.5rem;
    // No need for outline, using --focus-shadow on parent
    outline: none;
  }
}

/* Basic editor styles */
.ProseMirror {

  > * + * {
    margin-top: 0.25em;
  }

  ul,
  ol {
    padding: 0.2em 2em;
    margin: 0;
  }

  ul:not([data-type="taskList"]) {
    li {
      margin-left: 0.3em;
    }
  }

  ul[data-type="taskList"] {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;

      > label {
        flex: 0 0 auto;
        margin-right: 0.5rem;
      }
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
    font-variant: normal;
  }

  code {
    background-color: rgba(#616161, 0.1);
    color: #616161;
  }

  pre {
    background: #0D0D0D;
    color: #FFF;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }

  mark {
    background-color: #FAF594;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  hr {
    margin: 1rem 0;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0D0D0D, 0.1);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(#0D0D0D, 0.1);
    margin: 2rem 0;
  }

  a {
    color: $color-blue-text;

    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  u {
    text-decoration: underline !important;
  }

  p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
    font-style: italic;
  }

}
</style>
